import { Component, ElementRef, forwardRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as monaco from 'monaco-editor';
import { parseTmTheme } from 'monaco-themes';

// import monacoTheme from 'monaco-editor-themes/Katzenmilch.json';
// import monacoTheme from 'monaco-editor-themes/Slush and Poppies.json';
import monacoTheme from 'monaco-editor-themes/iPlasticScopeRoot.json';

@Component({
	selector: 'app-monaco-editor',
	templateUrl: './monaco-editor.component.html',
	styleUrl: './monaco-editor.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MonacoEditorComponent),
			multi: true
		}
	]
})
export class MonacoEditorComponent implements OnDestroy, OnInit, ControlValueAccessor {
	monacoEditor?: monaco.editor.IStandaloneCodeEditor;
	@Input() initialContent: string = "";
	@Input() readOnly = false;

	@ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;

	private onChange: (value: string) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private ngZone: NgZone) {}
	
	ngOnInit(): void {
		const model = monaco.editor.createModel(this.initialContent, 'json');
		monaco.editor.defineTheme(
			'scoperoot-monaco-theme',
			monacoTheme as monaco.editor.IStandaloneThemeData
		);

		this.monacoEditor = monaco.editor.create(
			this.editorContainer.nativeElement,
			{
				theme: 'scoperoot-monaco-theme',
				automaticLayout: true,
				readOnly: this.readOnly,
				language: "json",
				wordWrap: "on",
				model
			}
		);

		// const tmThemeString = (new FileReader()).

		/**
		 * Monaco manages its own event system and its callbacks may execute
		 * outside Angular's zone.
		 *
		 * ControlValueAccessor requires us to call onChange() whenever the
		 * editor value changes so that Angular's FormControl stays in sync.
		 *
		 * Running the callback inside Angular's NgZone ensures Angular is
		 * notified of the change and performs change detection. Without this,
		 * the FormControl value may update while template bindings that depend
		 * on that value (or values derived from valueChanges) fail to refresh.
		 */
		this.monacoEditor.onDidChangeModelContent(() => {
			const value = this.monacoEditor?.getValue() ?? '';

			this.ngZone.run(() => {
				this.onChange(value);
			});
		});
		
		/**
		 * Monaco blur events may likewise occur outside Angular's zone.
		 *
		 * Angular Forms uses the ControlValueAccessor's onTouched() callback
		 * to mark the control as touched, so execute it inside Angular's zone
		 * for the same reason as onChange().
		 */
		this.monacoEditor.onDidBlurEditorWidget(() => {
			this.onTouched();
		});
	}

	writeValue(value: string | null): void {
		const newValue = value ?? '';

		if (this.monacoEditor?.getValue() !== newValue) {
			this.monacoEditor?.setValue(newValue);
		}
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
		this.readOnly = disabled;

		this.monacoEditor?.updateOptions({
			readOnly: disabled
		});
	}

	ngOnDestroy() {
		if (this.monacoEditor) {
			const model = this.monacoEditor.getModel();
			this.monacoEditor.setModel(null);
			this.monacoEditor.dispose();
			model?.dispose();
			// model?.modified.dispose();
			
			// this.newDiffEditorEvent.emit(undefined);
		}
	}
}
